import { gql, useLazyQuery, useQuery } from "@apollo/client";
import { ICountryEntity, IFundingRoundEntity, VariableType } from "types";
import config from "config";

export const GET_FUNDING = gql`
  query getCountries($sort: [CountrySort!], $isAdmin: Boolean!) {
    countries(options: { sort: $sort }) {
      id
      name
      lat
      lng
      fundingUsdTotal
      fundingLocalTotal
      verticalFundings(where:{vertical: ${config.VERTICAL}}) {
        id
        vertical
        commentary
        nationalStrategy
        source {
          id
          name
          url
        }
      }
      fundingRounds(where:{
        vertical: ${config.VERTICAL}
      }) {
        id
        status
        createdAt
        fundingUsd
        fundingLocal
        fxRate
        lastUpdatedBy @include(if: $isAdmin)
        updatedAt
        localCurrency
        fundingInfo
        source
        isActive
        deliveryPeriod
        startDate
      }
    }
  }
`;

export function useGraphQLGovernmentFunding({ sort, isAdmin }: VariableType) {
  const { loading, error, data } = useQuery<
    {
      countries: ICountryEntity[];
    },
    VariableType
  >(GET_FUNDING, {
    fetchPolicy: "network-only",
    variables: {
      sort: sort,
      isAdmin: isAdmin || false,
    },
  });

  return { loading, error, data };
}

export const GET_ROUNDS = gql`
  query getGovRounds(
    $sort: [CountrySort!]
    $isAdmin: Boolean!
    $where: FundingRoundWhere
  ) {
    fundingRounds(where: $where) {
      id
      isGovernment
      createdAt
      fundingUsd
      fundingLocal
      fxRate
      lastUpdatedBy @include(if: $isAdmin)
      updatedAt
      localCurrency
      fundingInfo
      source
      isActive
      deliveryPeriod
      startDate
      country(options: { sort: $sort }) {
        id
        name
      }
    }
    fundingRoundsAggregate(where: $where) {
      count
    }
  }
`;

export function useGraphQLGovernmentFundingRounds({
  sort,
  isAdmin,
  where,
}: VariableType) {
  const { loading, error, data } = useQuery<
    {
      fundingRounds: IFundingRoundEntity[];
      fundingRoundsAggregate: { count: number };
    },
    VariableType
  >(GET_ROUNDS, {
    fetchPolicy: "network-only",
    variables: {
      where: {
        ...where,
        vertical: config.VERTICAL,
        isGovernment: true,
      },
      sort: sort,
      isAdmin: isAdmin || false,
    },
  });

  return { loading, error, data };
}

export function useGovernmentFundingExportQuery({
  sort,
  isAdmin,
}: VariableType) {
  const [getExportData, { loading: loadingExport, data: dataExport }] =
    useLazyQuery<{
      [key: string]: ICountryEntity[];
    }>(GET_FUNDING, {
      variables: {
        sort: sort,
        isAdmin: isAdmin || false,
      },
    });

  return { loadingExport, dataExport, getExportData };
}
